import { Injectable } from '@angular/core';
import {
  DocumentAttribute,
  DocumentReferenceBase,
  ReferencedDocument,
  VirusScanStatus,
} from '@qtek/shared/models';
import {
  filter,
  map,
  Observable,
  of,
  race,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { DocumentReferenceService } from './document-references/core/document-reference.service';

/** How a freshly uploaded doc's async virus scan settled. */
export type ScanOutcome = 'ready' | 'failed' | 'timeout';

const DEFAULT_TIMEOUT_MS = 120_000;

/** Servable = no scan pipeline (null) or scan passed. */
export function isServable(vsts: VirusScanStatus | null | undefined): boolean {
  return vsts == null || vsts === VirusScanStatus.OK;
}

function isScanFailure(vsts: VirusScanStatus | null | undefined): boolean {
  return (
    vsts === VirusScanStatus.CONTAMINATED || vsts === VirusScanStatus.FAILED
  );
}

/** Extract the reference matching `referenceId` from a `*DocumentReference` WS message, if present. */
function referenceNodeFor<T extends DocumentReferenceBase>(
  message: any,
  referenceId: string
): T | undefined {
  if (message?.op === 'del') {
    return undefined;
  }
  const res = message?.res;
  const nodes: T[] = Array.isArray(res) ? res : res ? [res] : [];
  return nodes.find(node => node?._id === referenceId);
}

export interface ScanGateReferenceParams {
  /** The `*DocumentReference` row's own `_id`, as returned by `list()`/`upload()` — NOT the underlying document id. */
  referenceId: string;
  attribute: DocumentAttribute;
  /** Owning entity id, scopes the WS subscription (e.g. `personId`, `itemId`). */
  entityId?: string;
  /** Scan status already known for the doc; lets servable docs resolve sync. */
  virusScanStatus?: VirusScanStatus | null;
  timeoutMs?: number;
}

/**
 * A freshly uploaded doc isn't servable until its async virus scan finishes
 * (the backend answers 425/503 until then). This gate opens a short-lived
 * `*DocumentReference` subscription scoped to the entity and emits once the
 * scan settles — the single source of truth for every v2 upload flow.
 */
@Injectable({ providedIn: 'root' })
export class DocumentScanGate {
  private seq = 0;

  /**
   * Every update to one reference's document — scan status and processing steps — for showing
   * progress rather than waiting for one outcome. Completes after `timeoutMs`, so a document that
   * never reports back can't hold the WS subscription open; unsubscribing earlier also ends it.
   */
  documentUpdates<T extends DocumentReferenceBase>(
    service: DocumentReferenceService<T>,
    referenceId: string,
    timeoutMs = DEFAULT_TIMEOUT_MS
  ): Observable<ReferencedDocument> {
    return service
      .subscribe(
        {
          mysid: `doc-updates-${referenceId}-${++this.seq}`,
          id: referenceId,
          cmd: 'get',
        },
        false
      )
      .pipe(
        map(message => referenceNodeFor<T>(message, referenceId)?.document),
        filter((document): document is ReferencedDocument => !!document),
        takeUntil(timer(timeoutMs))
      );
  }

  whenReferenceSettled<T extends DocumentReferenceBase>(
    service: DocumentReferenceService<T>,
    params: ScanGateReferenceParams
  ): Observable<ScanOutcome> {
    // Only short-circuit when currentVsts is known; omitted means "verify over WS".
    if (
      params.virusScanStatus !== undefined &&
      isServable(params.virusScanStatus)
    ) {
      return of('ready');
    }

    // Unique mysid per call so each retry issues a fresh query.
    const settled$ = service
      .subscribe(
        {
          mysid: `scan-gate-${params.referenceId}-${++this.seq}`,
          id: params.referenceId,
          cmd: 'get',
        },
        false
      )
      .pipe(
        map(message => referenceNodeFor<T>(message, params.referenceId)),
        // Act on a terminal scan status; ignore still-pending updates.
        map((node): ScanOutcome | undefined => {
          const vsts = node?.document?.virusScanStatus;
          if (!node) {
            return undefined;
          }
          if (isServable(vsts)) {
            return 'ready';
          }
          return isScanFailure(vsts) ? 'failed' : undefined;
        }),
        filter((outcome): outcome is ScanOutcome => !!outcome),
        take(1)
      );

    // Timer covers a scan that never reports back, so the caller can't hang.
    return race(
      settled$,
      timer(params.timeoutMs ?? DEFAULT_TIMEOUT_MS).pipe(
        map((): ScanOutcome => 'timeout')
      )
    );
  }
}
